<template>
    <div>
        <PageHeader :title="pageHeader">
            <template v-slot:icon>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="page-header-icon">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            </template>
        </PageHeader>

				<div class="bg-white p-4 mt-4" v-if="!loading">
				<h2 class="text-sm font-bold text-gray-600 uppercase">Legenda de Cores</h2>
					<div class="flex flex-wrap gap-4 mt-3">
						<div class="flex items-center gap-2">
							<div class="w-3 h-5 bg-blue-500 rounded-sm"></div>
							<span class="text-sm font-semibold text-gray-700">Gerente</span>
						</div>
						<div class="flex items-center gap-2">
							<div class="w-3 h-5 bg-green-500 rounded-sm"></div>
							<span class="text-sm font-semibold text-gray-700">Colaborador</span>
						</div>
						<div class="flex items-center gap-2">
							<div class="w-3 h-5 bg-yellow-500 rounded-sm"></div>
							<span class="text-sm font-semibold text-gray-700">Diretor</span>
						</div>
					</div>
				</div>

				<div class="flex justify-center">
					<Loading text="Buscando informações... ..." :show="loading" />
				</div>

				<div class="bg-white p-4 mt-4" v-if="subordinatesOwn.length">
					<h1 class="text-lg font-bold text-gray-600 uppercase mt-2">Seus Liderados</h1>
					<div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2 mt-4">
						<div
							v-for="worker in subordinatesOwn"
							:key="`own-${worker.id}`"
							@click="onSelectCompetencePDITarget(worker)"
								:class="[
									'flex items-center bg-white border border-gray-200 rounded overflow-hidden cursor-pointer transition-all hover:shadow-md',
									selectedWorkerPDITarget
										? worker.id === selectedWorkerPDITarget
											? 'opacity-100 ring-2 ring-green-600'
										: 'opacity-40'
									: 'opacity-100'
							]"
						>
							<div
								:class="[
									'w-2 h-10',
									worker.role === 'director'
										? 'bg-yellow-500'
										: worker.role === 'manager'
										? 'bg-blue-500'
										: 'bg-green-500'
								]"
							></div>
							<div class="px-3 py-2 flex-1 min-w-0">
								<span class="text-sm font-semibold text-gray-700 uppercase block truncate">{{ worker.name }}</span>
								<div class="flex items-center gap-1 mt-1">
									<span
										class="w-2.5 h-2.5 rounded-sm"
										:style="{ backgroundColor: getWorkerGroupInfo(worker.id).color }"
									></span>
									<span class="font-normal text-gray-400 uppercase truncate leading-none" style="font-size: 10px;">
										{{ getWorkerGroupInfo(worker.id).name }}
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="bg-white p-4 mt-4" v-if="groupedWorkers.length || searchTerm">
					<h1 class="text-lg font-bold text-gray-600 uppercase mt-2">Todos os Colaboradores</h1>
						<div class="mt-5 flex justify-center">
							<div class="w-full max-w-3xl flex items-center gap-3">
								<input
									v-model="searchTerm"
									type="text"
									placeholder="Pesquisar colaborador por nome..."
									class="w-full h-10 px-4 rounded-md border border-gray-300 text-sm text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
									@keyup.enter="onSearchSubordinates"
								/>
								<button
									type="button"
									class="h-10 px-4 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors"
									@click="onSearchSubordinates"
								>
								Pesquisar
							</button>
							</div>
						</div>

						<div v-if="loadingSearch" class="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
							<div
								v-for="index in 3"
								:key="`search-skeleton-${index}`"
								class="rounded border-2 border-gray-200 bg-white overflow-hidden animate-pulse"
							>
								<div class="h-10 bg-gray-200"></div>
								<div class="p-3 min-h-24 bg-gray-50">
									<div class="space-y-2">
										<div class="h-10 bg-white border border-gray-200 rounded"></div>
										<div class="h-10 bg-white border border-gray-200 rounded"></div>
									</div>
								</div>
							</div>
						</div>

					<div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
						<div
							v-for="group in groupedWorkers"
							:key="group.id"
							:class="[
								'rounded border-2 overflow-hidden',
								group.isUngrouped ? 'bg-gray-50 border-dashed border-gray-300' : 'bg-white',
								group.highlight && !group.isUngrouped ? 'ring-2 ring-green-300' : '',
								group.isUngrouped ? 'md:col-span-2 lg:col-span-3' : ''
							]"
							:style="{ borderColor: group.isUngrouped ? '#d1d5db' : (group.color || '#22c55e') }"
						>
							<div
								class="relative flex items-center justify-between px-4 pr-6 py-2 font-bold text-sm uppercase tracking-wide"
								:style="group.isUngrouped
									? { backgroundColor: '#f3f4f6', color: '#6b7280' }
									: { backgroundColor: group.color || '#22c55e', color: '#ffffff' }"
							>
								<span>{{ group.name }}</span>
							</div>

							<div :class="['p-3 min-h-24', group.isUngrouped ? 'bg-gray-100' : group.highlight ? 'bg-green-50' : 'bg-gray-50']">
								<div
									v-if="group.workers.length"
									:class="group.isUngrouped ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2' : 'space-y-2'"
								>
								<div
									v-for="worker in group.workers"
									:key="worker.id"
									@click="onSelectCompetencePDITarget(worker)"
									:class="[
										'flex items-center bg-white border border-gray-200 rounded overflow-hidden cursor-pointer transition-all hover:shadow-md',
										selectedWorkerPDITarget
											? worker.id === selectedWorkerPDITarget
												? 'opacity-100 ring-2 ring-green-600'
												: 'opacity-40'
											: 'opacity-100'
									]"
								>
								<div
									:class="[
										'w-2 h-10',
										worker.role === 'director'
											? 'bg-yellow-500'
											: worker.role === 'manager'
											? 'bg-blue-500'
											: 'bg-green-500'
									]"
								></div>
									<div class="px-3 py-2 flex-1 min-w-0">
										<span class="text-sm font-semibold text-gray-700 uppercase block truncate">{{ worker.name }}</span>
									</div>
								</div>
								</div>
								<div v-else class="text-sm text-gray-500">Sem colaboradores neste grupo.</div>
							</div>
						</div>
					</div>
				</div>

			<div class="grid grid-cols-1 mt-4" v-if="!subordinates.length && !loading && !loadingPDIRendering">
				<Alert :text="searchTerm ? 'Nenhum colaborador encontrado para essa busca' : 'Você não tem liderados'" :show="!subordinates.length && !loading" />
			</div>

				<div v-if="selectedWorkerPDITarget" class="bg-green-50 border border-green-200 rounded p-4 mt-4">
					<span class="text-xs font-bold text-green-700 uppercase">Colaborador selecionado</span>
					<p class="text-lg font-bold text-green-900 uppercase mt-1">
						{{ selectedWorkerName || '-' }}{{ selectedWorkerGroupName ? ' - ' + selectedWorkerGroupName : '' }}
					</p>
				</div>

			<div ref="criteriaSection" v-if="selectedWorkerPDITarget" class="bg-white p-4 mt-4">
				<h1 class="text-lg font-bold text-gray-600 uppercase mt-2 -mb-4">Critérios</h1>
					<div class="flex flex-col mt-6">
						<label class="label">Data de Competência</label>
						<Datepicker class="w-3/12" :clearable="false" locale="pt-BR" monthPicker v-model="competenceDate" @update:modelValue="onChangeCompetenteDate()" autoApply />
				</div>
			<div class="mt-4 flex gap-x-2">
				<div class="w-5 h-5 bg-pink-100 border border-gray-500"></div>
				<span class="text-sm font-semibold text-gray-600">Questões que só a Megaluzz Responde</span>
			</div>
		</div>

		<div v-if="loadingPDIRendering && selectedWorkerPDITarget" class="bg-white p-4">
			<!-- Loading Skeleton-->
			<div class="mt-4 mx-auto w-full rounded-md p-4 mb-4">
				<div class="flex animate-pulse space-x-4">
					<div class="size-10 rounded-full bg-gray-300"></div>
					<div class="flex-1 space-y-6 py-1">
					<div class="h-2 rounded bg-gray-300"></div>
					<div class="space-y-3">
						<div class="grid grid-cols-3 gap-4">
						<div class="col-span-2 h-2 rounded bg-gray-300"></div>
						<div class="col-span-1 h-2 rounded bg-gray-300"></div>
						</div>
						<div class="h-2 rounded bg-gray-300"></div>
					</div>
					</div>
				</div>
			</div>
		</div>

		<!-- FORM e TERMOMETRO -->
		<div v-if="selectedWorkerPDITarget && pdiForm && pdiForm.id" class="grid grid-cols-8 bg-white p-4">
			<div v-if="!loadingPDIRendering" class="col-span-6">
				<!-- FORM RENDER-->
				<div v-if="selectedWorkerPDITarget">
					<!-- LOOP Structure -->
					<div v-for="field in pdiForm.form_structure.structure" :key="field.uuid">
						
						<!-- Columns-->
						<div class="grid grid-cols-12">
							<div class="col-span-8 flex p-1 bg-gray-50 mt-2">
								
								<div class="flex flex-col w-full">
									<div class="flex">
										<div class="text-sm font-bold mr-2 w-6 h-6 text-center text-white border border-gray-500 rounded-full bg-green-800 p-0.5">{{ field.points || 0 }}</div>
										<label :class="{ 'bg-pink-100': field.lock }" class="text-base font-light text-gray-900">{{ field.label }}</label>
									</div>
									
									<div v-if="field.type == 'long_text'" class="mt-2">
										<textarea
											v-model="field.answer"
											@input="debouncedTextareaSave()"
											rows="3"
											class="w-full border border-gray-300 rounded p-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 resize-y"
										></textarea>
									</div>
								</div>
							</div>
							<div v-if="!field.fixed && !field.lock" class="col-span-4 flex gap-x-2 mt-2">
								<div v-for="option in field.options" :key="option.uuid" class="flex items-center gap-x-1 bg-green-50">
									<BaseRadio
									:value="option.label"
									:label="option.label"
									:name="field.uuid"
									v-model="field.answer"
									@update:modelValue="onAnswerChange(field, option)"
									/>
								</div>
							</div>
							<div v-if="(field.lock || field.fixed) && field.type !== 'long_text'" class="col-span-4 flex gap-x-2 mt-2">
								<h1 class="text-sm text-center mt-2 text-gray-600 font-bold p-1 rounded-full w-6 h-6 bg-pink-100">{{ field.answer }}</h1>
							</div>
						</div>

					</div> <!-- End Loop -->
				</div>
			</div>

			<div v-if="!loadingPDIRendering" class="col-span-2">
				<!-- {{ pdiForm.form_structure.score_result }} -->
				<!-- TERMOMETRO -->
				<PdiScore
					v-if="pdiForm.form_structure?.structure"
					:structure="pdiForm.form_structure.structure"
					:score_result="pdiForm.form_structure.score_result"
					:competence_label="competenceLabel"
					:show_bonus="hasWorkerBonusRules"
					:bonus_amount="formattedPdiBonusAmount"
					:bonus_rules="workerBonusRules"
				/>
			</div>
		</div>

    </div>
</template>

<script>
    import { ref, onMounted, computed, nextTick } from 'vue';
    import Swal from 'sweetalert';
	import { WorkerService, WorkerGroupService, PDIService } from '../services/Entity';
	import PdiScore from '../components/PdiScore.vue';
	import { toast } from 'vue3-toastify';
	import 'vue3-toastify/dist/index.css';
	import Alert from '../components/Alert.vue';

    export default {
        name: 'Forms',
        components: {
			PdiScore,
			Alert
        },
        setup() {
            const pageHeader = ref('PDI')
            const loading = ref(true);
            const loadingSearch = ref(false);
            const selectedWorkerPDITarget = ref();
			const subordinates = ref([]);
			const pdiForm = ref({
				form_structure: {
					structure: [],
					score_result: {}
				}
			});
			const now = new Date();
            const loadingPDIRendering = ref(false);
			const workerGroups = ref([]);
			const workerBonusRules = ref([]);
			const searchTerm = ref('');
			const competenceDate = ref({
				month: now.getMonth(), // porque JS é 0-based
				year: now.getFullYear()
			});
			const criteriaSection = ref(null);

			function getWorkerGroupRolePriority(role) {
				if (role === 'director') return 0;
				if (role === 'manager') return 1;
				return 2;
			}

			function compareWorkersForGroupSort(workerA, workerB) {
				const priorityDifference = getWorkerGroupRolePriority(workerA.role) - getWorkerGroupRolePriority(workerB.role);

				if (priorityDifference !== 0) {
					return priorityDifference;
				}

				const normalizedNameA = (workerA.name || '').trim().toLocaleLowerCase('pt-BR');
				const normalizedNameB = (workerB.name || '').trim().toLocaleLowerCase('pt-BR');
				const nameComparison = normalizedNameA.localeCompare(normalizedNameB, 'pt-BR', { sensitivity: 'base' });

				if (nameComparison !== 0) {
					return nameComparison;
				}

				return Number(workerA.id) - Number(workerB.id);
			}

			const groupedWorkers = computed(() => {
				const workers = Array.isArray(subordinates.value) ? subordinates.value : [];
				const groups = Array.isArray(workerGroups.value) ? workerGroups.value : [];
				const workersById = new Map();
				workers.forEach(worker => {
					workersById.set(worker.id, worker);
					workersById.set(String(worker.id), worker);
				});

					const normalizedGroups = groups.map(group => ({
						id: group.id,
						name: group.name,
						color: group.color,
						isUngrouped: false,
						isFallback: false,
						highlight: false,
						workers: (group.worker_ids || [])
						.map(workerId => workersById.get(workerId))
						.filter(Boolean)
						.sort(compareWorkersForGroupSort)
				})).filter(group => group.workers.length);

				const groupedWorkerIds = new Set(
					normalizedGroups.flatMap(group => group.workers.map(worker => worker.id))
				);
				const workersWithoutGroup = workers.filter(worker => !groupedWorkerIds.has(worker.id));

				if (!normalizedGroups.length) {
						return workersWithoutGroup.length ? [{
							id: 'fallback-ungrouped',
							name: 'Pessoas sem grupo',
							color: '#22c55e',
							isUngrouped: true,
							isFallback: true,
							highlight: true,
							workers: [...workersWithoutGroup].sort(compareWorkersForGroupSort)
					}] : [];
				}

				if (workersWithoutGroup.length) {
						normalizedGroups.push({
							id: 'ungrouped',
							name: 'Pessoas sem grupo',
							color: '#22c55e',
							isUngrouped: true,
							isFallback: false,
							highlight: true,
							workers: [...workersWithoutGroup].sort(compareWorkersForGroupSort)
					});
				}

				return normalizedGroups;
			});
			const selectedWorkerName = computed(() => {
				const selected = subordinates.value.find(worker => worker.id === selectedWorkerPDITarget.value);
				return selected?.name || '';
			});
			const selectedWorkerGroupName = computed(() => {
				if (!selectedWorkerPDITarget.value) return '';
				const groups = workerGroupInfoByWorkerId.value.get(String(selectedWorkerPDITarget.value)) || [];
				return groups.map(group => group.name).join(', ');
			});
			const pdiTotalScore = computed(() => {
				const structure = pdiForm.value?.form_structure?.structure || [];

				return structure.reduce((total, field) => {
					const value = Number(field.answer);
					return total + (Number.isNaN(value) ? 0 : value);
				}, 0);
			});
			const hasWorkerBonusRules = computed(() => Array.isArray(workerBonusRules.value) && workerBonusRules.value.length > 0);
			const matchedPdiBonusRule = computed(() => {
				if (!hasWorkerBonusRules.value) {
					return null;
				}

				return workerBonusRules.value.find(rule => {
					const scoreStart = Number(rule.score_start);
					const scoreEnd = Number(rule.score_end);

					if (Number.isNaN(scoreStart) || Number.isNaN(scoreEnd)) {
						return false;
					}

					return pdiTotalScore.value >= scoreStart && pdiTotalScore.value <= scoreEnd;
				}) || null;
			});
			const pdiBonusAmount = computed(() => {
				if (!matchedPdiBonusRule.value) {
					return 0;
				}

				const amount = Number(matchedPdiBonusRule.value.bonus_amount);
				return Number.isNaN(amount) ? 0 : amount;
			});
			const formattedPdiBonusAmount = computed(() => new Intl.NumberFormat('pt-BR', {
				style: 'currency',
				currency: 'BRL'
			}).format(pdiBonusAmount.value));
			const competenceLabel = computed(() => {
				if (!competenceDate.value || typeof competenceDate.value.month === 'undefined' || !competenceDate.value.year) {
					return '';
				}

				const competence = new Date(competenceDate.value.year, Number(competenceDate.value.month), 1);
				const formatted = competence.toLocaleDateString('pt-BR', {
					month: 'long',
					year: 'numeric'
				});

				return formatted.charAt(0).toUpperCase() + formatted.slice(1);
			});
			const subordinatesOwn = computed(() =>
				subordinates.value.filter(worker => worker.see_global !== true)
			);
			const workerGroupInfoByWorkerId = computed(() => {
				const map = new Map();
				const groups = Array.isArray(workerGroups.value) ? workerGroups.value : [];

				groups.forEach(group => {
					const workerIds = Array.isArray(group.worker_ids) ? group.worker_ids : [];
					workerIds.forEach(workerId => {
						const key = String(workerId);
						const currentGroups = map.get(key) || [];
						currentGroups.push({
							name: group.name || 'Sem grupo',
							color: group.color || '#22c55e'
						});
						map.set(key, currentGroups);
					});
				});

				return map;
			});

			onMounted(async () => {
				subordinates.value = await getSubordinates(searchTerm.value);
				await loadWorkerGroups();
			});

			async function getSubordinates(search = '', options = {}) {
				const { usePageLoading = true } = options;
				try {
					if (usePageLoading) loading.value = true;
					const { data: { subordinates } } = await WorkerService.getSubordinates(search);
					if (usePageLoading) loading.value = false;
					return subordinates;
				} catch(err) {
					if (usePageLoading) loading.value = false;

					const status = err?.response?.status;
					const errorMessage = err?.response?.data?.error;
					const workerNotFoundForUser = status === 404 && errorMessage === 'Worker não encontrado para o e-mail do usuário.';

					if (workerNotFoundForUser) {
						Swal({
							title: 'Acesso não permitido',
							text: 'Você não tem permissão para acessar essa área',
							icon: 'info'
						});
						return [];
					}

					console.log(err);
					Swal({
						title: 'Ocorreu um erro',
						text: 'Atualize a página e tente novamente',
						icon: 'error'
					});
					return [];
				}
			}

			async function onSearchSubordinates() {
				loadingSearch.value = true;
				selectedWorkerPDITarget.value = null;
				subordinates.value = await getSubordinates(searchTerm.value, { usePageLoading: false });
				loadingSearch.value = false;
			}

			function getWorkerGroupInfo(workerId) {
				return workerGroupInfoByWorkerId.value.get(String(workerId)) || {
					name: 'Sem grupo',
					color: '#9ca3af'
				};
			}

			async function loadWorkerGroups() {
				try {
					const { data } = await WorkerGroupService.list();
					workerGroups.value = Array.isArray(data) ? data : [];
				} catch (err) {
					workerGroups.value = [];
				}
			}

			async function loadWorkerBonusRules(workerId = selectedWorkerPDITarget.value, competence = competenceDate.value) {
				if (!workerId || !competence) {
					workerBonusRules.value = [];
					return;
				}

				try {
					const { data } = await WorkerService.getBonusRules(
						workerId,
						competence.month,
						competence.year
					);

					workerBonusRules.value = Array.isArray(data) ? data : [];
				} catch (err) {
					workerBonusRules.value = [];
					console.error('Erro ao carregar bonificação do PDI:', err);
				}
			}

			async function onChangeCompetenteDate() {
				const selectedCompetence = {
					month: competenceDate.value.month,
					year: competenceDate.value.year
				};
					const payload = {
						target: selectedWorkerPDITarget.value,
						competence_month: selectedCompetence.month,
						competence_year: selectedCompetence.year
					};

				loadingPDIRendering.value = true;
				try {
					const { data } = await PDIService.findOrCreate(payload);
					pdiForm.value = data;
					await loadWorkerBonusRules(selectedWorkerPDITarget.value, selectedCompetence);
				} catch (err) {
					if (err?.data?.error === 'pdi_form_not_configured') {
						Swal({
							title: 'Nenhum Megaform configurado',
							text: 'Nao existe formulario configurado para esse colaborador nem para o gestor responsavel.',
							icon: 'warning'
						});
						return;
					}

					throw err;
				} finally {
					loadingPDIRendering.value = false;
				}
			}

				async function scrollToCriteriaSection() {
					await nextTick();
					if (criteriaSection.value?.scrollIntoView) {
						criteriaSection.value.scrollIntoView({ behavior: 'smooth', block: 'start' });
					}
				}

				async function onSelectCompetencePDITarget(selectedWorker) {
					selectedWorkerPDITarget.value = selectedWorker.id;
					await scrollToCriteriaSection();
					const selectedCompetence = {
						month: competenceDate.value.month,
						year: competenceDate.value.year
					};

						const payload = {
							target: selectedWorkerPDITarget.value,
						competence_month: selectedCompetence.month,
						competence_year: selectedCompetence.year
					};

				loadingPDIRendering.value = true;
				try {
					const { data } = await PDIService.findOrCreate(payload);
					pdiForm.value = data;
					await loadWorkerBonusRules(selectedWorker.id, selectedCompetence);
				} catch (err) {
					if (err?.data?.error === 'pdi_form_not_configured') {
						Swal({
							title: 'Nenhum Megaform configurado',
							text: 'Nao existe formulario configurado para esse colaborador nem para o gestor responsavel.',
							icon: 'warning'
						});
						return;
					}

					throw err;
				} finally {
					loadingPDIRendering.value = false;
				}
			}

			async function onAnswerChange(field, option) {
				try {
					await PDIService.update(pdiForm.value.id, {
						form_structure: pdiForm.value.form_structure
					});

					notify('salvo!');
				} catch(err) {
					console.log(err);
				}
			}

			function debounce(fn, delay) {
				let timeout;
				return function (...args) {
					clearTimeout(timeout);
					timeout = setTimeout(() => fn.apply(this, args), delay);
				};
			}

			const debouncedTextareaSave = debounce(async function () {
				try {
					await PDIService.update(pdiForm.value.id, {
						form_structure: pdiForm.value.form_structure
					});

					notify('salvo!');
				} catch (err) {
					console.error('Erro ao salvar:', err);
				}
			}, 1000);

			function notify(notificationText) {
				toast(`${notificationText || ''}`, {
					"theme": "colored",
					"type": "success",
					"dangerouslyHTMLString": true,
					autoClose: 500
				});
			}

            return {
				onAnswerChange,
				debouncedTextareaSave,
				pdiForm,
				loadingPDIRendering,
				competenceDate,
				onChangeCompetenteDate,
                    onSelectCompetencePDITarget,
                    subordinates,
                    selectedWorkerPDITarget,
                    loading,
                    loadingSearch,
                    pageHeader,
					workerBonusRules,
					hasWorkerBonusRules,
					formattedPdiBonusAmount,
					competenceLabel,
					groupedWorkers,
					subordinatesOwn,
					workerGroups,
					searchTerm,
					onSearchSubordinates,
					getWorkerGroupInfo,
					criteriaSection,
					selectedWorkerName,
					selectedWorkerGroupName,
            }
        }
    }

</script>
